import { APIError } from 'components/core/errors/api'
import { introspect as authIntrospect } from 'lib/actions/auth'
import { introspectUser } from 'lib/actions/user'
import { UserContext, UserContextRecord } from 'lib/context/app'
import { AuthReducer, initialCachedAuthState } from 'lib/reducers/auth'
import { initialUsersState, UsersReducer } from 'lib/reducers/users'

import { GetReturn, Status } from 'lib/rpc'
import { useClientRPC } from 'lib/rpcts'
import { IntrospectResponse } from 'pbts/c1/api/auth/v1/auth'
import { AuthClient } from 'pbts/c1/api/auth/v1/auth.client'
import { UserServiceClient } from 'pbts/c1/api/user/v1/user.client'
import { ReactNode, useEffect, useReducer, useState } from 'react'

const updateUserContextFromAuth = (
    userContext: UserContextRecord,
    auth: GetReturn<IntrospectResponse>,
): UserContextRecord => {
    if (auth.status === Status.ERROR || auth.status === Status.LOADING || auth.status === Status.INIT) {
        return userContext.set('status', auth.status)
    }
    return userContext
        .set('roles', new Set<string>(auth.item.roles))
        .set('permissions', new Set<string>(auth.item.permissions))
        .set('features', new Set<string>(auth.item.features))
        .set('status', auth.status)
}

let accept
const promise = new Promise((a) => { accept = a })
export const UserContextProvider = ({ children }: { children: ReactNode }): React.ReactElement => {
    const authClient = useClientRPC<AuthClient>(AuthClient)
    const userClient = useClientRPC(UserServiceClient)
    const [authState, authDispatch] = useReducer(AuthReducer, initialCachedAuthState)
    const [userState, userDispatch] = useReducer(UsersReducer, initialUsersState)

    const auth = authState.get('item')
    const [userContext, setUserContext] = useState<UserContextRecord>((): UserContextRecord => {
        const record = updateUserContextFromAuth(new UserContextRecord(), auth)

        return record.set('user', {
            ...record.get('user'),
            get id(): string {
                // NOTE: this is the supsense API
                //  we do this to avoid race conditions on userID existing
                throw promise
            },
        }).set('waitForReady', (): void => { throw promise })
    })
    const userResponse = userState.get('item')
    useEffect(() => {
        if (authClient === undefined) {
            return
        }
        authIntrospect(authDispatch, authClient)
    }, [authClient])

    useEffect(() => {
        setUserContext((prevUserContext) => updateUserContextFromAuth(prevUserContext, auth))
    }, [auth])

    useEffect(() => {
        if (userClient === undefined) {
            return
        }
        introspectUser(userDispatch, userClient)
    }, [userClient])

    useEffect(() => {
        if (
            userResponse.status === Status.ERROR ||
            userResponse.status === Status.LOADING ||
            userResponse.status === Status.INIT
        ) {
            return
        }

        // eslint-disable-next-line @typescript-eslint/no-empty-function
        setUserContext((prevUserContext) => prevUserContext.set('user', userResponse.item).set('waitForReady', () => {}))
        accept()
    }, [userResponse])

    // Let ErrorBoundary handle the error.
    if (userResponse.status === Status.ERROR) {
        return <APIError err={userResponse.error} />
    }

    return <UserContext.Provider value={userContext}>
        {children}
    </UserContext.Provider>
}
